import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useCountry } from '../../components/CountrySelector';
import { useOrderSummary, GET_ORDER_SUMMARY_QUERY } from '../useOrderSummary';
import { UpdateOrderAction, OrderState } from '../useUpdateOrder';
import { PREVIEW_ORDER_SHIPPING_ADDRESS_UPDATE_MUTATION } from './usePreviewOrderShippingAddressUpdate.rest';

export const usePreviewOrderShippingAddressUpdate = (orderNumber: string) => {
  const {
    country: { alpha2Code, alpha3Code }
  } = useCountry();

  const { orderSummary } = useOrderSummary(orderNumber);

  const [mutate, { loading, error }] = useMutation(PREVIEW_ORDER_SHIPPING_ADDRESS_UPDATE_MUTATION, {
    update: (cache, { data }) => {
      if (!data) return;

      cache.writeQuery({
        query: GET_ORDER_SUMMARY_QUERY,
        variables: { countryCode: alpha2Code, orderNumber },
        data: {
          getOrderSummary: {
            __typename: 'RestOrderSummaryPayload',
            data: data.previewOrderShippingAddressUpdate.data,
            included: data.previewOrderShippingAddressUpdate.included
          }
        }
      });
    }
  });

  const previewOrderShippingAddressUpdate = useCallback(
    async (selectedAddressId: string) => {
      if (!orderSummary) return;

      await mutate({
        variables: {
          orderId: orderSummary.id,
          input: {
            data: {
              action_type: UpdateOrderAction.ChangeAddress,
              id: orderSummary.id,
              type: 'order_summary',
              attributes: {
                country_code: alpha3Code,
                order_state: OrderState.Default,
                selected_address_id: selectedAddressId
              }
            }
          }
        }
      });
    },
    [alpha3Code, mutate, orderSummary]
  );

  return {
    previewOrderShippingAddressUpdate,
    loading,
    error
  };
};
